import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { AccountSettings, useAuthenticator } from "@aws-amplify/ui-react";
import { Form, Field } from "react-final-form";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import {
  FetchUserAttributesOutput,
  fetchUserAttributes,
  updateUserAttribute,
} from "aws-amplify/auth";
import { generateClient } from "aws-amplify/api";
import Stripe from "stripe";
import type { GetServerSideProps } from "next";
import { getCurrentUser } from "@aws-amplify/auth/server";
import { updateUser } from "../src/graphql/mutations";
import { fetchPostJSON } from "../utils/api-helpers";
import ContentLayoutFooterNoChartreuse from "../components/Layout/ContentLayoutFooterNoChartreuse";
import { UpdateUserInput } from "../src/API";
import { runWithAmplifyServerContext } from "../utils/amplifyServerUtils";

export const getServerSideProps: GetServerSideProps = async ({ req, res }) => {
  try {
    const user = await runWithAmplifyServerContext({
      nextServerContext: { request: req, response: res },
      operation: (contextSpec) => getCurrentUser(contextSpec),
    });
    const data = await fetchPostJSON(
      `${process.env.NEXT_PUBLIC_API_URL}/api/remaining`,
      {
        id: user.userId,
      },
    );
    let promotionCodes = null;
    if (data.stripeCustomerId !== "null") {
      const stripe = new Stripe(process.env.STRIPE_SECRET_KEY!, {
        apiVersion: "2024-06-20",
      });
      const promoParam: Stripe.PromotionCodeListParams = {
        customer: data.stripeCustomerId,
        active: true,
        expand: ["data.coupon"],
      };
      promotionCodes = await stripe.promotionCodes.list(promoParam);
      console.log(
        "Here are the promotional codes for the user: ",
        promotionCodes,
      );
    }
    return {
      props: {
        remainingCredits: data.remainingGenerations,
        remainingGiftCard: promotionCodes,
      },
    };
  } catch (err) {
    console.log(err);
    return {
      props: {
        remainingCredits: null,
        remainingGiftCard: null,
      },
    };
  }
};

function Account({ remainingCredits, remainingGiftCard }) {
  const client = generateClient();
  const [lastfour, setLastfour] = useState("");
  const [usernameEdit, setUsernameEdit] = useState<boolean>();
  const [passwordEdit, setPasswordEdit] = useState<boolean>();
  const router = useRouter();
  const [userAttributes, setUserAttributes] =
    useState<FetchUserAttributesOutput>({});
  const { authStatus, user } = useAuthenticator((context) => [
    context.authStatus,
    context.user,
  ]);

  async function handleFetchUserAttributesEmail() {
    try {
      const attributes = await fetchUserAttributes();
      setUserAttributes(attributes);
      return attributes;
    } catch (error) {
      console.log(error);
      return "";
    }
  }

  useEffect(() => {
    if (authStatus === "unauthenticated") {
      router.push("/login");
    }
    if (authStatus === "authenticated" && user) {
      handleFetchUserAttributesEmail();
    }
  }, [authStatus, user, router]);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const handleSuccess = () => {
    alert("Password is successfully changed!");
  };

  const handleUpdateUserAttribute = async (values) => {
    try {
      const output = await updateUserAttribute({
        userAttribute: {
          attributeKey: "preferred_username",
          value: values.username,
        },
      });
      console.log(output);
      const input: UpdateUserInput = {
        id: user.userId,
        username: values.username,
      };
      const updateUserResponse = await client.graphql({
        query: updateUser,
        variables: {
          input,
        },
      });
      console.log(updateUserResponse);
      setUsernameEdit(false);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    function replaceFirstFourCharsWithXXXX(inputString) {
      if (inputString.length < 4) {
        setLastfour(
          "XXXX-".substring(0, inputString.length) +
            inputString.substring(inputString.length),
        );
      }
      setLastfour(`****${inputString.substring(4)}`);
    }
    if (
      authStatus === "authenticated" &&
      remainingGiftCard &&
      remainingGiftCard.data.length > 0
    ) {
      replaceFirstFourCharsWithXXXX(remainingGiftCard.data[0].code);
    }
  }, [authStatus, remainingGiftCard, router]);

  function editPassword() {
    setPasswordEdit(true);
  }

  function editUserName() {
    setUsernameEdit(true);
  }

  // function handlePasswordUpdate() {}

  const required = (value) => (value ? undefined : "Required");

  return (
    <ContentLayoutFooterNoChartreuse title="Account">
      {authStatus === "authenticated" && (
        <div className={isSmallScreen ? "px-10 py-5" : "px-20 py-10"}>
          <p className="text-xl font-black pb-5">
            Welcome, {userAttributes.email}
          </p>
          <p className="text-xl font-black py-5">Account Dashboard</p>
          {remainingCredits && (
            <span className="block items-center py-2">
              Current Credits:&nbsp;
              {remainingCredits}
            </span>
          )}
          {remainingGiftCard && remainingGiftCard.data.length > 0 && (
            <>
              <p className="block items-center">Current Gift Card Balance:</p>
              <p className="block items-center mb-4">
                {lastfour}:&nbsp;$
                {(remainingGiftCard.data[0].coupon.amount_off / 100).toFixed(2)}
              </p>
            </>
          )}

          <p className="mt-5 text-xl font-black py-5">Sign In Details</p>

          <div className="flex flex-row w-full">
            <div className="w-1/3">
              <span className="block items-center py-5">Email</span>
            </div>
            <div className="w-1/3">
              <span className="block items-center py-5">
                {userAttributes.email}
              </span>
            </div>
            <div className="w-1/3 flex items-center justify-end" />
          </div>

          {!usernameEdit && (
            <div className="flex flex-row w-full">
              <div className="w-1/3">
                <span className="block items-center py-5">Username</span>
              </div>
              <div className="w-1/3">
                <span className="block items-center py-5">
                  {userAttributes.preferred_username}
                </span>
              </div>
              <div className="w-1/3 flex items-center justify-end">
                <button
                  type="submit"
                  className="bg-black text-white py-1 px-4 rounded-2xl hover:bg-gray-800 focus:outline-none active:bg-gray-800"
                  onClick={editUserName}
                >
                  Edit
                </button>
              </div>
            </div>
          )}
          {usernameEdit && (
            <Form
              onSubmit={handleUpdateUserAttribute}
              render={({ handleSubmit, form }) => {
                const { submitting } = form.getState();
                return (
                  <div className="w-full flex flex-col justify-center items-center">
                    <form className="w-full" onSubmit={handleSubmit}>
                      <div className="amplify-flex amplify-field gap-2">
                        <Field name="username" validate={required}>
                          {({ input, meta }) => (
                            <>
                              <label className="amplify-flex amplify-label">
                                Username
                              </label>
                              <input
                                {...input}
                                type="username"
                                placeholder="Enter a new Username"
                                className="amplify-input amplify-field-group--horizontal"
                              />
                              {meta.error && meta.touched && (
                                <span className="text-red-500 hover:text-red-700">
                                  {meta.error}
                                </span>
                              )}
                            </>
                          )}
                        </Field>
                        <div className="w-full my-4 flex items-center justify-between">
                          <button
                            type="submit"
                            className="w-full bg-black text-white py-2 px-4 mr-4 hover:bg-gray-800 focus:outline-none active:bg-gray-800"
                            disabled={submitting}
                            onClick={() => setUsernameEdit(false)}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="w-full bg-black text-white py-2 px-4 hover:bg-gray-800 focus:outline-none active:bg-gray-800"
                            disabled={submitting}
                          >
                            Save Changes
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                );
              }}
            />
          )}

          {!passwordEdit && (
            <div className="flex flex-row w-full">
              <div className="w-1/3">
                <span className="block items-center py-5">Password</span>
              </div>
              <div className="w-1/3">
                <span className="block items-center py-5">********</span>
              </div>
              <div className="w-1/3 flex items-center justify-end py-5">
                <button
                  type="submit"
                  className="bg-black text-white py-1 px-4 rounded-2xl hover:bg-gray-800 focus:outline-none active:bg-gray-800"
                  onClick={editPassword}
                >
                  Edit
                </button>
              </div>
            </div>
          )}

          {passwordEdit && (
            <>
              <AccountSettings.ChangePassword onSuccess={handleSuccess} />
              <button
                type="submit"
                className="w-full bg-black text-white py-2 px-4 mt-4 hover:bg-gray-800 focus:outline-none active:bg-gray-800"
                onClick={() => setPasswordEdit(false)}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      )}
    </ContentLayoutFooterNoChartreuse>
  );
}

export default Account;
